import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Visibility,
  ShoppingCart,
  Category,
  Refresh,
  PersonOutline,
  Download,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/ui/PageHeader';
import { BehaviorService } from '../services/behaviorService';

const CustomerBehaviorDashboard: React.FC = () => {
  const navigate = useNavigate();
  
  // State for filters
  const [period, setPeriod] = useState<'24h' | '7d' | '30d'>('7d');
  const [customerId, setCustomerId] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  
  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Fetch product views
  const { data: productViewsData, isLoading: loadingViews, refetch: refetchViews } = useQuery({
    queryKey: ['behavior', 'product-views', period, customerId, category, page, rowsPerPage],
    queryFn: () => BehaviorService.getProductViews({
      customer_id: customerId || undefined,
      category: category || undefined,
      period: period,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch trending products
  const { data: trendingData, isLoading: loadingTrending } = useQuery({
    queryKey: ['behavior', 'trending-products', period],
    queryFn: () => BehaviorService.getTrendingProducts({ period: period, limit: 10 }),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch catalogue metrics
  const { data: catalogueData, isLoading: loadingCatalogue } = useQuery({
    queryKey: ['behavior', 'catalogue-views', period],
    queryFn: () => BehaviorService.getCatalogueViews({ period: period }),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch product interest metrics
  const { data: interestData, isLoading: loadingInterest } = useQuery({
    queryKey: ['behavior', 'product-interest', period],
    queryFn: () => BehaviorService.getProductInterest({ period: period }),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch seen/not seen stats
  const { data: seenNotSeenData, isLoading: loadingSeenNotSeen } = useQuery({
    queryKey: ['behavior', 'seen-not-seen', customerId],
    queryFn: () => BehaviorService.getSeenNotSeen({ customer_id: customerId || undefined }),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch open rates
  const { data: openRatesData, isLoading: loadingOpenRates } = useQuery({
    queryKey: ['behavior', 'open-rates', period],
    queryFn: () => BehaviorService.getOpenRates({ entity_type: 'catalogue', period: period }),
    staleTime: 5 * 60 * 1000,
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewCustomer = (customerId: string) => {
    navigate(`/admin/parties/customers/${customerId}`);
  };

  const handleExportProductViews = async () => {
    try {
      const blob = await BehaviorService.exportProductViews({
        customer_id: customerId || undefined,
        category: category || undefined,
        period: period,
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `product_views_${period}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting product views:', error);
      // You could add a toast notification here
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-IN').format(value);
  };

  const isLoading = loadingViews || loadingTrending || loadingCatalogue || 
                    loadingInterest || loadingSeenNotSeen || loadingOpenRates;

  if (isLoading && !productViewsData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const productViews = productViewsData?.views || [];
  const totalViews = productViewsData?.total || 0;
  const trendingProducts = trendingData || [];
  const catalogueViews = catalogueData?.views || [];
  const interestMetrics = interestData;
  const seenNotSeenStats = seenNotSeenData;
  const openRates = openRatesData || [];

  // Prepare data for interest levels chart
  const interestLevelsData = interestMetrics ? [
    { name: 'High Interest', value: interestMetrics.high_interest?.length || 0, color: '#00C49F' },
    { name: 'Medium Interest', value: interestMetrics.medium_interest?.length || 0, color: '#FFBB28' },
    { name: 'Low Interest', value: interestMetrics.low_interest?.length || 0, color: '#FF8042' },
  ] : [];

  return (
    <Box>
      <PageHeader
        title="Customer Behavior Analytics"
        subtitle="Track customer interactions, product views, and engagement metrics"
      />

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Time Period</InputLabel>
              <Select
                value={period}
                label="Time Period"
                onChange={(e) => {
                  setPeriod(e.target.value as '24h' | '7d' | '30d');
                  setPage(0);
                }}
              >
                <MenuItem value="24h">Last 24 Hours</MenuItem>
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Customer ID (optional)"
              value={customerId}
              onChange={(e) => {
                setCustomerId(e.target.value);
                setPage(0);
              }}
              placeholder="Filter by customer"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Category (optional)"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setPage(0);
              }}
              placeholder="Filter by category"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => refetchViews()}
            >
              Refresh
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={1}>
            <Tooltip title="Export to CSV">
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<Download />}
                onClick={handleExportProductViews}
              >
                Export
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Key Metrics */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Tooltip title="Total number of product views in the selected period">
            <Card sx={{ cursor: 'help' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Total Product Views
                    </Typography>
                    <Typography variant="h4">{formatNumber(totalViews)}</Typography>
                  </Box>
                  <Visibility sx={{ fontSize: { xs: 36, md: 48 }, color: '#0088FE', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Tooltip>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Tooltip title="Products with the highest view counts">
            <Card sx={{ cursor: 'help' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Trending Products
                    </Typography>
                    <Typography variant="h4">{trendingProducts.length}</Typography>
                  </Box>
                  <TrendingUp sx={{ fontSize: { xs: 36, md: 48 }, color: '#00C49F', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Tooltip>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Tooltip title="Number of catalogues viewed by customers">
            <Card sx={{ cursor: 'help' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Catalogue Views
                    </Typography>
                    <Typography variant="h4">{catalogueViews.length}</Typography>
                  </Box>
                  <Category sx={{ fontSize: { xs: 36, md: 48 }, color: '#FFBB28', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Tooltip>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Tooltip title="Percentage of products that have been viewed by customers">
            <Card sx={{ cursor: 'help' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Coverage
                    </Typography>
                    <Typography variant="h4">
                      {seenNotSeenStats ? `${seenNotSeenStats.coverage_percentage.toFixed(1)}%` : 'N/A'}
                    </Typography>
                  </Box>
                  <ShoppingCart sx={{ fontSize: { xs: 36, md: 48 }, color: '#8884D8', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Tooltip>
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} mb={3}>
        {/* Trending Products Chart */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">
                  Top Trending Products
                </Typography>
                <Tooltip title="Products ranked by total views and unique customer count">
                  <IconButton size="small">
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={trendingProducts.slice(0, 5)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="product_name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={100}
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis style={{ fontSize: '12px' }} />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="total_views" fill="#0088FE" name="Total Views" />
                  <Bar dataKey="unique_customers" fill="#00C49F" name="Unique Customers" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Product Interest Levels */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">
                  Product Interest Levels
                </Typography>
                <Tooltip title="High: >50 views or >10 customers | Medium: 10-50 views | Low: <10 views">
                  <IconButton size="small">
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={interestLevelsData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {interestLevelsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Seen/Not Seen Statistics */}
      {seenNotSeenStats && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Product Coverage Statistics
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center">
                  <Typography variant="h3" color="primary">
                    {formatNumber(seenNotSeenStats.seen_products)}
                  </Typography>
                  <Typography color="textSecondary">Products Viewed</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center">
                  <Typography variant="h3" color="error">
                    {formatNumber(seenNotSeenStats.not_seen_products)}
                  </Typography>
                  <Typography color="textSecondary">Products Not Viewed</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center">
                  <Typography variant="h3" color="success.main">
                    {seenNotSeenStats.coverage_percentage.toFixed(1)}%
                  </Typography>
                  <Typography color="textSecondary">Coverage Rate</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Open Rate Metrics */}
      {openRates.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Catalogue Open Rates
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#0d47a1' }}>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Catalogue</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Open Rate</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Customers Viewed</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Total Eligible</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">Trend</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {openRates.map((rate) => (
                    <TableRow key={rate.entity_id} hover>
                      <TableCell>{rate.entity_name}</TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={`${rate.open_rate_percentage.toFixed(1)}%`}
                          color={rate.open_rate_percentage > 50 ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">{formatNumber(rate.customers_who_viewed)}</TableCell>
                      <TableCell align="right">{formatNumber(rate.total_eligible_customers)}</TableCell>
                      <TableCell align="center">
                        {rate.trend === 'up' && <TrendingUp color="success" />}
                        {rate.trend === 'down' && <TrendingDown color="error" />}
                        {rate.trend === 'stable' && <Typography variant="body2">—</Typography>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Product Views Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Product Views
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#0d47a1' }}>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Product</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>View Time</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Duration (s)</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productViews.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="textSecondary" py={3}>
                        No product views found for the selected period
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  productViews.map((view) => (
                    <TableRow key={`${view.customer_id}-${view.product_id}-${view.view_timestamp}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {view.customer_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {view.customer_email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {view.product_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          SKU: {view.product_sku}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {view.category && <Chip label={view.category} size="small" />}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(view.view_timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {view.view_duration_seconds || 'N/A'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Customer Profile">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewCustomer(view.customer_id)}
                          >
                            <PersonOutline />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={totalViews}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default CustomerBehaviorDashboard;
